import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { uploadConnectorService } from '../../services/uploadConnector';
import { useToast } from '../../hooks/use-toast';
import { PresetInputDialog } from '../jobs/PresetInputDialog';
import {
  Sparkles,
  Wand2,
  Loader2,
  X,
  Check,
  Zap,
  Calculator,
  ArrowRight,
  Move,
  FileImage,
  Palette,
  Settings,
  Eye,
  Brain,
  Tag,
  Shirt,
  Home,
  Star,
  Smartphone,
} from 'lucide-react';

const FAVORITES_STORAGE_KEY = 'transformation-preset-favorites';

interface Transformation {
  type: string;
  [key: string]: any;
}

interface InputField {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  options?: string | { value: string; label: string }[];
  defaultValue?: string;
}

interface TransformationPreset {
  id: string;
  name: string;
  description: string;
  transformations: Transformation[];
  plan?: string;
  estimatedCredits?: number;
  processingTime?: string;
  requiresInput?: boolean;
  inputFields?: InputField[];
}

interface AssetTransformationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedImageIds: string[];
  storeId: string;
  onProcessingStarted: (batchId?: string, taskId?: string, imageCount?: number) => void;
}

// Helper to check if transformation is AI-based
const isAITransformation = (type: string): boolean => {
  const aiTypes = [
    'background_removal',
    'ai_upscale',
    'generative_fill',
    'generative_remove',
    'generative_replace',
    'generative_background_replace',
    'auto_tagging',
    'object_detection',
    'quality_enhancement',
    'auto_enhancement',
    'noise_reduction',
    'invisible_mannequin',
    'object_placement',
  ];
  return aiTypes.includes(type);
};

// Helper to format transformation type for display
const formatTransformationType = (type: string): string => {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export function AssetTransformationPanel({
  open,
  onOpenChange,
  selectedImageIds,
  storeId,
  onProcessingStarted,
}: AssetTransformationPanelProps) {
  const { toast } = useToast();
  const [presets, setPresets] = useState<Record<string, TransformationPreset>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [selectedTransformations, setSelectedTransformations] = useState<Transformation[]>([]);
  const [creditInfo, setCreditInfo] = useState<{
    total_credits: number;
    credits_per_image: number;
    breakdown: Array<{ type: string; total_credits: number; description: string }>;
  } | null>(null);
  const [isCalculatingCredits, setIsCalculatingCredits] = useState(false);
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [activeTab, setActiveTab] = useState<'all' | 'favorites'>(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      const favs = stored ? JSON.parse(stored) : [];
      return favs.length > 0 ? 'favorites' : 'all';
    } catch {
      return 'all';
    }
  });
  const [showPresetInputDialog, setShowPresetInputDialog] = useState(false);
  const [selectedPresetForInput, setSelectedPresetForInput] = useState<TransformationPreset | null>(null);

  const imageCount = selectedImageIds.length;

  // Load presets on mount
  useEffect(() => {
    if (open) {
      loadPresets();
      // Reset state when opening
      setSelectedPreset(null);
      setSelectedTransformations([]);
      setCreditInfo(null);
      setShowPresetInputDialog(false);
      setSelectedPresetForInput(null);
      // Open favorites tab if user has favorites
      setActiveTab(favorites.length > 0 ? 'favorites' : 'all');
    }
  }, [open]);

  // Calculate credits when transformations change
  useEffect(() => {
    if (selectedTransformations.length > 0) {
      calculateCredits(selectedTransformations);
    } else {
      setCreditInfo(null);
    }
  }, [selectedTransformations, imageCount]);

  // Save favorites to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      // Ignore localStorage errors
    }
  }, [favorites]);

  const toggleFavorite = (presetKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites((prev) =>
      prev.includes(presetKey)
        ? prev.filter((key) => key !== presetKey)
        : [...prev, presetKey]
    );
  };

  const isFavorite = (presetKey: string) => favorites.includes(presetKey);

  // Filter presets based on active tab
  const filteredPresets = Object.entries(presets).filter(([key]) => {
    if (activeTab === 'favorites') {
      return favorites.includes(key);
    }
    return true;
  });

  const loadPresets = async () => {
    setIsLoading(true);
    try {
      const result = await uploadConnectorService.getTransformationPresets();
      setPresets(result.presets || {});
    } catch (err) {
      console.error('Failed to load presets:', err);
      toast({
        title: 'Error',
        description: 'Failed to load transformation presets',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateCredits = async (transformations: Transformation[]) => {
    if (!storeId || transformations.length === 0) return;

    setIsCalculatingCredits(true);
    try {
      const result = await uploadConnectorService.calculateCredits(
        storeId,
        transformations,
        imageCount
      );
      setCreditInfo(result);
    } catch (err) {
      console.error('Failed to calculate credits:', err);
    } finally {
      setIsCalculatingCredits(false);
    }
  };

  const handlePresetSelect = (key: string) => {
    const preset = presets[key];
    if (preset) {
      // Check if preset requires input (like virtual staging)
      if (preset.requiresInput && preset.inputFields && preset.inputFields.length > 0) {
        setSelectedPresetForInput(preset);
        setShowPresetInputDialog(true);
      } else {
        setSelectedPreset(key);
        // Copy transformations from preset
        setSelectedTransformations([...preset.transformations]);
      }
    }
  };

  const handlePresetInputConfirm = (updatedTransformations: Transformation[]) => {
    if (selectedPresetForInput) {
      // Find the key for the preset
      const presetKey = Object.entries(presets).find(
        ([_, p]) => p.id === selectedPresetForInput.id || p.name === selectedPresetForInput.name
      )?.[0];
      if (presetKey) {
        setSelectedPreset(presetKey);
      }
      setSelectedTransformations(updatedTransformations);
    }
    setShowPresetInputDialog(false);
    setSelectedPresetForInput(null);
  };

  const handleRemoveTransformation = (index: number) => {
    setSelectedTransformations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProcess = async () => {
    if (selectedTransformations.length === 0) return;

    setIsProcessing(true);
    try {
      const result = await uploadConnectorService.processImages(
        storeId,
        selectedImageIds,
        selectedTransformations
      );

      // Pass batchId and taskId for real-time progress tracking
      onProcessingStarted(result.processingBatchId, result.taskId, result.imageCount);
      onOpenChange(false);

      // Only show toast if not using Python parallel (no taskId means no floating toast)
      if (!result.taskId) {
        toast({
          title: 'Processing Started',
          description: result.message,
        });
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to start processing',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getPresetIcon = (key: string) => {
    if (key.includes('upscale')) return <Zap className="h-4 w-4" />;
    if (key.includes('background')) return <Wand2 className="h-4 w-4" />;
    if (key.includes('mannequin')) return <Shirt className="h-4 w-4" />;
    if (key.includes('staging') || key.includes('placement')) return <Home className="h-4 w-4" />;
    if (key.includes('social') || key.includes('reels') || key.includes('tiktok')) return <Smartphone className="h-4 w-4" />;
    return <Sparkles className="h-4 w-4" />;
  };

  const getTransformationIcon = (type: string) => {
    switch (type) {
      case 'background_removal':
        return <Sparkles className="w-4 h-4" />;
      case 'resize':
        return <Move className="w-4 h-4" />;
      case 'format_conversion':
        return <FileImage className="w-4 h-4" />;
      case 'ai_upscale':
        return <Zap className="w-4 h-4" />;
      case 'color_adjustment':
        return <Palette className="w-4 h-4" />;
      case 'generative_fill':
        return <Wand2 className="w-4 h-4" />;
      case 'generative_remove':
        return <Eye className="w-4 h-4" />;
      case 'generative_replace':
      case 'generative_background_replace':
        return <Brain className="w-4 h-4" />;
      case 'auto_tagging':
        return <Tag className="w-4 h-4" />;
      case 'object_detection':
        return <Eye className="w-4 h-4" />;
      case 'invisible_mannequin':
        return <Shirt className="w-4 h-4" />;
      case 'object_placement':
        return <Home className="w-4 h-4" />;
      case 'quality_enhancement':
      case 'auto_enhancement':
        return <Sparkles className="w-4 h-4" />;
      case 'sharpen':
        return <Zap className="w-4 h-4" />;
      default:
        return <Settings className="w-4 h-4" />;
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Apply Transformations
          </DialogTitle>
          <DialogDescription>
            Select a transformation preset to apply to {imageCount} selected image
            {imageCount !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : Object.keys(presets).length === 0 ? (
            <Alert>
              <AlertDescription>
                No transformation presets available for your plan.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Presets List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Presets
                  </h3>
                  <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'all' | 'favorites')}>
                    <TabsList className="h-8">
                      <TabsTrigger value="all" className="text-xs px-3">
                        All
                      </TabsTrigger>
                      <TabsTrigger value="favorites" className="text-xs px-3">
                        <Star className="h-3 w-3 mr-1" />
                        Favorites ({favorites.length})
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <div className="h-[500px] overflow-y-auto pr-4">
                  {activeTab === 'favorites' && favorites.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No favorite presets yet</p>
                        <p className="text-xs mt-1">Click the star icon on any preset to add it</p>
                      </div>
                    </div>
                  ) : (
                  <div className="space-y-2">
                    {filteredPresets.map(([key, preset]) => (
                      <Card
                        key={key}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedPreset === key
                            ? 'border-primary ring-2 ring-primary/20'
                            : 'hover:border-primary/50'
                        }`}
                        onClick={() => handlePresetSelect(key)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div
                              className={`p-2 rounded-lg ${
                                selectedPreset === key
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted'
                              }`}
                            >
                              {getPresetIcon(key)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-sm truncate">
                                  {preset.name}
                                </h4>
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {preset.description}
                              </p>
                              {preset.estimatedCredits && (
                                <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                                  <Calculator className="h-3 w-3" />
                                  <span>~{preset.estimatedCredits} credits/image</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={(e) => toggleFavorite(key, e)}
                                className={`p-1 rounded-full transition-colors ${
                                  isFavorite(key)
                                    ? 'text-yellow-500 hover:text-yellow-600'
                                    : 'text-muted-foreground/50 hover:text-yellow-500'
                                }`}
                                title={isFavorite(key) ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                <Star
                                  className="h-4 w-4"
                                  fill={isFavorite(key) ? 'currentColor' : 'none'}
                                />
                              </button>
                              {selectedPreset === key && (
                                <Check className="h-5 w-5 text-primary" />
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  )}
                </div>
              </div>

              {/* Selected Transformations */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  Selected Transformations
                </h3>

                {selectedTransformations.length > 0 ? (
                  <Card className="bg-muted/30">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        {selectedPreset && getPresetIcon(selectedPreset)}
                        {selectedPreset && presets[selectedPreset]?.name}
                      </CardTitle>
                      <CardDescription>
                        {selectedPreset && presets[selectedPreset]?.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Transformations List with Remove Buttons */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium">Transformations ({selectedTransformations.length})</h4>
                          {selectedTransformations.length === 0 && selectedPreset && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePresetSelect(selectedPreset)}
                              className="text-xs"
                            >
                              Reset
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {selectedTransformations.map((t, index) => {
                            const isAI = isAITransformation(t.type);
                            return (
                              <div
                                key={index}
                                className={`flex items-center justify-between p-2 rounded-lg border-l-4 ${
                                  isAI
                                    ? 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-500'
                                    : 'bg-accent/30 border-primary/30'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-primary/10 text-primary">
                                    {index + 1}
                                  </div>
                                  {getTransformationIcon(t.type)}
                                  <span className="text-sm">{formatTransformationType(t.type)}</span>
                                  {isAI && (
                                    <Badge className="text-xs text-white bg-gradient-to-r from-blue-500 to-purple-500">
                                      <Sparkles className="mr-1 w-3 h-3" />
                                      AI
                                    </Badge>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveTransformation(index)}
                                  className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                        {selectedTransformations.length === 0 && (
                          <Alert className="mt-2">
                            <AlertDescription className="text-sm">
                              All transformations removed. Select a preset again to restore them.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>

                      {/* Credit Info */}
                      {isCalculatingCredits ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Calculating credits...
                        </div>
                      ) : creditInfo ? (
                        <div className="p-3 bg-background rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-muted-foreground">
                              Images to process
                            </span>
                            <span className="font-medium">{imageCount}</span>
                          </div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-muted-foreground">
                              Credits per image
                            </span>
                            <span className="font-medium">
                              {creditInfo.credits_per_image.toFixed(1)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t">
                            <span className="text-sm font-medium">Total Credits</span>
                            <span className="text-lg font-bold text-primary">
                              {creditInfo.total_credits.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      ) : null}

                      {/* Processing Time Estimate */}
                      {selectedPreset && presets[selectedPreset]?.processingTime && (
                        <div className="text-sm text-muted-foreground">
                          Estimated time: {presets[selectedPreset].processingTime}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="h-[300px] flex items-center justify-center border-2 border-dashed rounded-lg">
                    <div className="text-center text-muted-foreground">
                      <Wand2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Select a preset to see transformations</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={handleProcess}
            disabled={selectedTransformations.length === 0 || isProcessing || isCalculatingCredits}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4 mr-1" />
                Process {imageCount} Image{imageCount !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Preset Input Dialog for virtual staging and other presets requiring input */}
    {selectedPresetForInput && (
      <PresetInputDialog
        preset={selectedPresetForInput as any}
        isOpen={showPresetInputDialog}
        onClose={() => {
          setShowPresetInputDialog(false);
          setSelectedPresetForInput(null);
        }}
        onConfirm={handlePresetInputConfirm}
      />
    )}
    </>
  );
}
